import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../stores/notificationStore';
import { NotificationType, Notification } from '../types/notifications';
import { formatTimeAgoCompact } from '../utils/time';
import { requestNotificationPermission } from '../services/pushNotificationService';
import toast from 'react-hot-toast';
import {
  Briefcase,
  Users,
  MessageCircle,
  Calendar,
  PenLine,
  Check,
  AlarmClock,
  Megaphone,
  Heart,
  Bell,
} from 'lucide-react';

interface NotificationDropdownProps {
    onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { notifications, markAsRead } = useNotificationStore();

    useEffect(() => {
        // Automatically prompt for notification permission if not previously decided
        if ('Notification' in window && window.Notification.permission === 'default') {
            requestNotificationPermission().then((token) => {
                if (token) {
                    // FCM Token obtained from dropdown interaction
                    toast.success(t('toasts.push_enabled'));
                }
            });
        }
    }, []);

    const handleNotificationClick = async (notification: Notification) => {
        if (!notification.read_at) {
            await markAsRead(notification.id);
        }

        if (notification.action_url) {
            navigate(notification.action_url);
        }
        onClose();
    };

    const getNotificationIcon = (type: NotificationType) => {
        const iconClass = "h-5 w-5";
        switch (type) {
            case 'job_applicant':
                return <Briefcase className={iconClass} />;
            case 'meetup_interest':
                return <Users className={iconClass} />;
            case 'chat_message':
                return <MessageCircle className={iconClass} />;
            case 'meetup_updated':
                return <Calendar className={iconClass} />;
            case 'job_updated':
                return <PenLine className={iconClass} />;
            case 'application_status_changed':
                return <Check className={iconClass} />;
            case 'meetup_reminder':
                return <AlarmClock className={iconClass} />;
            case 'system_announcement':
                return <Megaphone className={iconClass} />;
            case 'post_like':
                return <Heart className={iconClass} />;
            case 'post_comment':
                return <MessageCircle className={iconClass} />;
            default:
                return <Bell className={iconClass} />;
        }
    };

    // Get only the 5 most recent notifications
    const recentNotifications = notifications.slice(0, 5);

    return (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 flex flex-col overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
            <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                <h3 className="font-semibold text-gray-900">{t('notif_dropdown.title')}</h3>
            </div>

            <div className="max-h-[360px] overflow-y-auto">
                {recentNotifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">
                        {t('notif_dropdown.empty')}
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50">
                        {recentNotifications.map((notification) => (
                            <button
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${!notification.read_at ? 'bg-blue-50/50' : ''
                                    }`}
                            >
                                <div className="flex-shrink-0 mt-0.5 text-lg">
                                    {getNotificationIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm ${!notification.read_at ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                        {notification.title}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                        {notification.message}
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-1 font-medium">
                                        {formatTimeAgoCompact(notification.created_at)}
                                    </p>
                                </div>
                                {!notification.read_at && (
                                    <div className="flex-shrink-0 mt-1.5 align-middle">
                                        <span className="h-2 w-2 bg-blue-600 rounded-full inline-block"></span>
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="border-t border-gray-50 p-2 bg-gray-50/50">
                <button
                    onClick={() => {
                        navigate('/notifications');
                        onClose();
                    }}
                    className="w-full text-center py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                >
                    {t('notif_dropdown.view_all')}
                </button>
            </div>
        </div>
    );
};

export default NotificationDropdown;
